import { Badge } from "@component/badge";
import { Button } from "@component/button";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { COUNTRY_CODES } from "@component/country-codes-data";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { Tooltip } from "@component/tooltip";
import { useSnackbar } from "@hook/use-snackbar";
import { useSubscription } from "@context/subscription_context";
import { useUserProfileContext } from "@context/user_profile";
import { getCompanyBillingInfo, updateCompanyBillingInfo } from "@service/user";
import { CompanyBillingInfo } from "@type/company";
import { parseFormat } from "@util/date";
import { isValidEmail } from "@util/string";
import {
    STRIPE_SUBSCRIPTION_ENABLED,
    resolveBillingPlatform,
} from "@util/billing-platform";
import BillingPaymentDetails from "@container/billing-payment-details";
import { InfoIcon, SquarePenIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { mapping_subscription_package } from "@constant/subscription";

interface BillingPlanOverviewProps {
    setShowRequestInfoDialog: (show: boolean) => void;
    formatPrice: (price: number) => string;
}

export default function BillingPlanOverview({
    setShowRequestInfoDialog,
    formatPrice,
}: BillingPlanOverviewProps) {
    const snackbar = useSnackbar();
    const { subscriptionActive, isLoadingSubscriptionActive, reminderExpiredDay } = useSubscription();
    const [, userInfo] = useUserProfileContext();
    // Match BillingPaymentDetails' OWN availability gate (Stripe flag on AND a
    // resolvable billing platform) so exactly one of the shared surface / the
    // legacy Pricing Plan card always renders — never a blank Plan Overview (#2808
    // review): the old card was gated on the flag alone, but the shared surface
    // also needs a resolvable platform.
    const hasSharedPaymentDetails = useMemo(
        () =>
            STRIPE_SUBSCRIPTION_ENABLED && Boolean(resolveBillingPlatform(userInfo)),
        [userInfo],
    );
    const [isLoadingUpdateBillingInfo, setIsLoadingUpdateBillingInfo] = useState(false);
    const [isEditingBillingInfo, setIsEditingBillingInfo] = useState(false);
    const [billingInfo, setBillingInfo] = useState<CompanyBillingInfo | null>(null);
    const [editedBillingInfo, setEditedBillingInfo] = useState<CompanyBillingInfo | null>(null);

    useEffect(() => {
        getCompanyBillingInfo().then((resp) => {
            if (resp.status === 200) {
                setEditedBillingInfo(resp.data.data);
                setBillingInfo(resp.data.data);
            }
        })
    }, [])

    const handleSaveBillingInfo = () => {
        if (isValidEmail(editedBillingInfo?.billing_email || "") === false) {
            snackbar.showError("Please enter a valid billing email address.");
            return;
        }

        setIsLoadingUpdateBillingInfo(true);
        updateCompanyBillingInfo(editedBillingInfo!).then((resp) => {
            if (resp.status === 200) {
                setBillingInfo(editedBillingInfo);
                setIsEditingBillingInfo(false);
                snackbar.showSuccess("Billing information updated successfully.");
            }
        }).catch(() => {
            snackbar.showError("Failed to update billing information. Please try again.");
        }).finally(() => {
            setIsLoadingUpdateBillingInfo(false);
        })
    }

    const handleCancelEditBillingInfo = () => {
        setEditedBillingInfo(billingInfo);
        setIsEditingBillingInfo(false);
    }

    return (
        <>
            <div className="space-y-8">
                {/* Plans + Payment Method (shared surface), Plans at the top.
                    Shown only when the shared surface can actually render. */}
                {hasSharedPaymentDetails && (
                    <BillingPaymentDetails
                        showCurrentSubscription={false}
                        plansFirst
                    />
                )}

                {/* OLD "Pricing Plan" card — the fallback whenever the shared
                    surface is unavailable (flag off OR no resolvable platform), so
                    one pricing section always shows (#2808 review). */}
                {!hasSharedPaymentDetails && (<>
                {/* Current Plan Details - Horizontal Layout */}
                <div className="flex gap-8">
                    {/* Left: Header and Description */}
                    <div className="w-80 flex-shrink-0">
                        <h3 className="text-lg font-semibold mb-1">Pricing Plan</h3>
                        <p className="text-sm text-muted-foreground">
                            Manage your pricing and plan features.
                        </p>
                    </div>

                    {/* Right: Plan Options */}
                    <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-6">
                            {/* Trial Plan */}
                            <Card className="relative border-2 border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 max-w-[480px]">
                                <Badge
                                    variant="default"
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 hover:bg-blue-600 py-1 rounded-full pointer-events-none"
                                >
                                    Current Plan
                                </Badge>
                                <CardHeader>
                                    <CardTitle className="text-xl">
                                        {subscriptionActive?.subscription_name || ""}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {!subscriptionActive?.is_trial && (
                                        <div>
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-3xl font-bold">
                                                    {formatPrice(subscriptionActive?.subscription_package_details.price_month || 0)}
                                                </span>
                                                <span className="text-muted-foreground">
                                                    / month
                                                </span>
                                            </div>
                                            <p className="text-sm text-muted-foreground italic">
                                                {formatPrice(subscriptionActive?.subscription_package_details.price || 0)} billed <span className="lowercase">{mapping_subscription_package[subscriptionActive?.subscription_package_id || ""]?.subscription_plan_wording || ""}</span>
                                            </p>
                                        </div>
                                    )}
                                    <div
                                        className="space-y-3"
                                        style={{
                                            paddingTop: 0,
                                            paddingRight: 0,
                                            paddingBottom: 30,
                                            paddingLeft: 0,
                                        }}
                                    >
                                        <h4 className="font-semibold text-sm">
                                            Inclusive of
                                        </h4>
                                        {!isLoadingSubscriptionActive && (
                                            <ul className="space-y-2 text-sm">
                                                {subscriptionActive?.subscription_details.cv_screening && (
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                                        <span>{subscriptionActive?.subscription_details.cv_screening} CV screenings</span>
                                                    </li>
                                                )}
                                                {subscriptionActive?.subscription_details.ai_assessment && (
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                                        <div className="flex items-center gap-1">
                                                            <span>{subscriptionActive?.subscription_details.ai_assessment} completed AI assessments</span>
                                                            <Tooltip content="Only completed interviews count towards your monthly quota.">
                                                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                            </Tooltip>
                                                        </div>
                                                    </li>
                                                )}
                                                {!subscriptionActive?.is_trial && (
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                                        <span>Unlimited users</span>
                                                    </li>
                                                )}
                                                {subscriptionActive?.subscription_details.full_ats_workflow && (
                                                    <li className="flex items-start gap-2">
                                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                                        <span>Full ATS and candidate workflow</span>
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </div>

                                    {(!isLoadingSubscriptionActive && subscriptionActive?.is_trial) && (
                                        <div className="pt-2">
                                            {subscriptionActive?.is_expired ? (
                                                <p className="text-sm font-medium text-red-600 mb-3">
                                                    Your access has expired. <br /> Please renew your plan to continue enjoying our services.
                                                </p>
                                            ) : subscriptionActive?.ended_at && reminderExpiredDay >= 0 && (
                                                <p className="text-sm font-medium text-muted-foreground mb-3">
                                                    Access expiring in:{" "}
                                                    <span className="font-semibold text-foreground">
                                                            {reminderExpiredDay} {reminderExpiredDay > 1 ? "days" : "day"} ({parseFormat(subscriptionActive?.ended_at || "", "D MMM YYYY")})
                                                    </span>
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Button moved to bottom */}
                                    {subscriptionActive?.is_trial && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowRequestInfoDialog(true)}
                                            className="w-full h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white hover:text-white border-blue-600 hover:border-blue-700"
                                        >
                                            Get in Touch
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* Horizontal Divider */}
                <div className="border-t border-border" />
                </>)}

                {/* Billing Information - Horizontal Layout */}
                <div className="flex gap-8">
                    {/* Left: Header and Description */}
                    <div className="w-80 flex-shrink-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-semibold mb-1">
                                    Billing Information
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    Manage your billing details. These details will appear
                                    on your invoices.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right: Billing Details */}
                    <div className="flex-1 mb-10">
                        <div className="space-y-6">
                            {/* Billing Details Section */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-1.5">
                                        <Label className="text-sm font-medium">
                                            Billing Details
                                        </Label>
                                        <Tooltip
                                            content={(
                                                <p>
                                                    These billing details default to the Workspace
                                                    Owner and can be updated at any time.
                                                </p>
                                            )}
                                        >
                                            <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                        </Tooltip>
                                    </div>

                                    {/* Edit/Save/Cancel Buttons aligned to the right */}
                                    {!isEditingBillingInfo ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setIsEditingBillingInfo(true)}
                                            className="h-8 px-2"
                                        >
                                            <SquarePenIcon className="h-4 w-4 mr-1" />
                                            Edit Billing Information
                                        </Button>
                                    ) : (
                                        <div className="flex gap-3">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleCancelEditBillingInfo}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                                onClick={handleSaveBillingInfo}
                                                disabled={editedBillingInfo?.billing_city === "" || editedBillingInfo?.billing_state_province === "" || editedBillingInfo?.billing_zip_postal === "" || editedBillingInfo?.billing_country === "" || editedBillingInfo?.billing_address === "" || isLoadingUpdateBillingInfo}
                                            >
                                                {isLoadingUpdateBillingInfo ? "Saving..." : "Save Changes"}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    {/* Billing Name */}
                                    <div>
                                        <Label
                                            htmlFor="billing-name"
                                            className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                        >
                                            Billing Name
                                        </Label>
                                        {isEditingBillingInfo ? (
                                            <Input
                                                id="billing-name"
                                                type="text"
                                                value={editedBillingInfo?.billing_name}
                                                onChange={(e) => {
                                                    setEditedBillingInfo({
                                                        ...editedBillingInfo!,
                                                        billing_name: e.target.value,
                                                    });
                                                }}
                                                placeholder="Enter billing name"
                                                className="w-full"
                                            />
                                        ) : (
                                            <Input
                                                id="billing-name-view"
                                                type="text"
                                                value={editedBillingInfo?.billing_name}
                                                disabled
                                                className="w-full bg-muted/50"
                                            />
                                        )}
                                    </div>

                                    {/* Billing Company */}
                                    <div>
                                        <Label
                                            htmlFor="billing-company"
                                            className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                        >
                                            Billing Company
                                        </Label>
                                        {isEditingBillingInfo ? (
                                            <Input
                                                id="billing-company"
                                                type="text"
                                                value={editedBillingInfo?.billing_company}
                                                onChange={(e) => {
                                                    setEditedBillingInfo({
                                                        ...editedBillingInfo!,
                                                        billing_company: e.target.value,
                                                    });
                                                }}
                                                placeholder="Enter billing company"
                                                className="w-full"
                                            />
                                        ) : (
                                            <Input
                                                id="billing-company-view"
                                                type="text"
                                                value={editedBillingInfo?.billing_company}
                                                disabled
                                                className="w-full bg-muted/50"
                                            />
                                        )}
                                    </div>

                                    {/* Billing Email */}
                                    <div>
                                        <Label
                                            htmlFor="billing-email"
                                            className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                        >
                                            Billing Email
                                        </Label>
                                        {isEditingBillingInfo ? (
                                            <Input
                                                id="billing-email"
                                                type="email"
                                                value={editedBillingInfo?.billing_email}
                                                onChange={(e) => {
                                                    setEditedBillingInfo({
                                                        ...editedBillingInfo!,
                                                        billing_email: e.target.value,
                                                    });
                                                }}
                                                placeholder="Enter billing email"
                                                className="w-full"
                                            />
                                        ) : (
                                            <Input
                                                id="billing-email-view"
                                                type="email"
                                                value={editedBillingInfo?.billing_email}
                                                disabled
                                                className="w-full bg-muted/50"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Billing Address */}
                            <div>
                                <Label className="text-sm font-medium mb-3 block">
                                    Billing Address
                                </Label>
                                {isEditingBillingInfo ? (
                                    <div className="space-y-4">
                                        {/* Street Address */}
                                        <div>
                                            <Label
                                                htmlFor="street-address"
                                                className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                            >
                                                Street Address <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id="street-address"
                                                value={editedBillingInfo?.billing_address}
                                                onChange={(e) =>
                                                    setEditedBillingInfo({
                                                        ...editedBillingInfo!,
                                                        billing_address: e.target.value,
                                                    })
                                                }
                                                placeholder="123 Business Street"
                                                className="w-full"
                                            />
                                        </div>

                                        {/* Unit/Apartment */}
                                        <div>
                                            <Label
                                                htmlFor="unit-apartment"
                                                className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                            >
                                                Unit / Suite / Floor
                                            </Label>
                                            <Input
                                                id="unit-apartment"
                                                value={editedBillingInfo?.billing_unit_floor}
                                                onChange={(e) =>
                                                    setEditedBillingInfo({
                                                        ...editedBillingInfo!,
                                                        billing_unit_floor: e.target.value,
                                                    })
                                                }
                                                placeholder="e.g. Suite 100, Apt 2B, Floor 3"
                                                className="w-full"
                                            />
                                        </div>

                                        {/* City and State */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label
                                                    htmlFor="city"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    City <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="city-view"
                                                    value={editedBillingInfo?.billing_city}
                                                    className="w-full"
                                                    onChange={(e) => {
                                                        setEditedBillingInfo({
                                                            ...editedBillingInfo!,
                                                            billing_city: e.target.value,
                                                        })
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Label
                                                    htmlFor="state"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    State / Province <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="state-view"
                                                    value={editedBillingInfo?.billing_state_province}
                                                    className="w-full"
                                                    onChange={(e) => {
                                                        setEditedBillingInfo({
                                                            ...editedBillingInfo!,
                                                            billing_state_province: e.target.value,
                                                        })
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Postcode and Country */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label
                                                    htmlFor="postcode"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    Postcode / ZIP <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="postcode"
                                                    value={editedBillingInfo?.billing_zip_postal}
                                                    onChange={(e) =>
                                                        setEditedBillingInfo({
                                                            ...editedBillingInfo!,
                                                            billing_zip_postal: e.target.value,
                                                        })
                                                    }
                                                    placeholder="94105"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div>
                                                <Label
                                                    htmlFor="country"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    Country <span className="text-red-500">*</span>
                                                </Label>
                                                <Select
                                                    value={editedBillingInfo?.billing_country}
                                                    onValueChange={(value: string) => {
                                                        setEditedBillingInfo({
                                                            ...editedBillingInfo!,
                                                            billing_country: value,
                                                        })
                                                    }}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue
                                                            placeholder="Select a country"
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {COUNTRY_CODES.map((country) => (
                                                            <SelectItem
                                                                key={country.code}
                                                                value={country.name}
                                                                onClick={() =>
                                                                    setEditedBillingInfo({
                                                                        ...editedBillingInfo!,
                                                                        billing_country: country.name,
                                                                    })
                                                                }
                                                            >
                                                                {country.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Street Address - View Mode */}
                                        <div>
                                            <Label
                                                htmlFor="street-address-view"
                                                className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                            >
                                                Street Address <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id="street-address-view"
                                                value={editedBillingInfo?.billing_address || "N/A"}
                                                disabled
                                                className="w-full bg-muted/50"
                                            />
                                        </div>

                                        {/* Unit/Apartment - View Mode */}
                                        <div>
                                            <Label
                                                htmlFor="unit-apartment-view"
                                                className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                            >
                                                Unit / Suite / Floor
                                            </Label>
                                            <Input
                                                id="unit-apartment-view"
                                                value={editedBillingInfo?.billing_unit_floor || "N/A"}
                                                placeholder="e.g. Suite 100, Apt 2B, Floor 3"
                                                disabled
                                                className="w-full bg-muted/50"
                                            />
                                        </div>

                                        {/* City and State - View Mode */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label
                                                    htmlFor="city-view"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    City <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="city-view"
                                                    value={editedBillingInfo?.billing_city || "N/A"}
                                                    disabled
                                                    className="w-full bg-muted/50"
                                                />
                                            </div>
                                            <div>
                                                <Label
                                                    htmlFor="state-view"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    State / Province <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="state-view"
                                                    value={editedBillingInfo?.billing_state_province || "N/A"}
                                                    disabled
                                                    className="w-full bg-muted/50"
                                                />
                                            </div>
                                        </div>

                                        {/* Postcode and Country - View Mode */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label
                                                    htmlFor="postcode-view"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    Postcode / ZIP <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="postcode-view"
                                                    value={editedBillingInfo?.billing_zip_postal || "N/A"}
                                                    disabled
                                                    className="w-full bg-muted/50"
                                                />
                                            </div>
                                            <div>
                                                <Label
                                                    htmlFor="country-view"
                                                    className="text-xs font-medium text-muted-foreground mb-1.5 block"
                                                >
                                                    Country <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="country-view"
                                                    value={editedBillingInfo?.billing_country || "N/A"}
                                                    disabled
                                                    className="w-full bg-muted/50"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* <div className="border-t border-border" />
                <div className="flex gap-8">
                    <div className="w-80 flex-shrink-0">
                        <h3 className="text-lg font-semibold mb-1">Payment Method</h3>
                        <p className="text-sm text-muted-foreground">
                            Manage your payment method
                        </p>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="space-y-6">
                            <div>
                                <p className="text-sm font-medium mb-1">Card Number</p>
                                <p className="text-sm text-muted-foreground">
                                    •••• •••• •••• 4242
                                </p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <Button variant="outline" size="sm">
                                    Update Payment Method
                                </Button>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>
        </>
    )
}